import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './ui.css';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';

const Select = ({
    id,
    label,
    description,
    error,
    placeholder = "Select an option",
    options = [],
    value,
    onChange,
    multiple = false,
    searchable = false,
    clearable = false,
    onFocus,
    onOpen,
    className = "",
    disabled = false,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const selectRef = useRef(null);
    const dropdownRef = useRef(null);
    const controlRef = useRef(null);

    // Calculate dropdown position
    const updateDropdownPosition = () => {
        if (controlRef.current) {
            const rect = controlRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 8, // Use viewport coordinates for fixed positioning
                left: rect.left,
                width: rect.width
            });
        }
    };

    // Update position when opening or window resizes
    useEffect(() => {
        if (isOpen) {
            updateDropdownPosition();
            const handleResize = () => updateDropdownPosition();
            const handleScroll = () => updateDropdownPosition();
            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleScroll, true);
            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleScroll, true);
            };
        }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!isOpen) return;

        // Use a small delay to prevent immediate closing on click
        const timeoutId = setTimeout(() => {
            const handleClickOutside = (event) => {
                if (
                    selectRef.current &&
                    !selectRef.current.contains(event.target) &&
                    dropdownRef.current &&
                    !dropdownRef.current.contains(event.target)
                ) {
                    setIsOpen(false);
                    setSearchTerm("");
                    setHighlightedIndex(-1);
                }
            };

            // Use 'click' instead of 'mousedown' to avoid immediate closing
            document.addEventListener('click', handleClickOutside, true);
            return () => {
                document.removeEventListener('click', handleClickOutside, true);
            };
        }, 10);

        return () => clearTimeout(timeoutId);
    }, [isOpen]);

    // Handle focus/open events
    const handleFocus = () => {
        if (onFocus) onFocus();
        if (onOpen) onOpen();
        if (!disabled) {
            setIsOpen(true);
        }
    };

    const handleToggle = (e) => {
        if (disabled) return;
        e.stopPropagation(); // Prevent event bubbling
        if (isOpen) {
            setIsOpen(false);
            setSearchTerm("");
            setHighlightedIndex(-1);
        } else {
            handleFocus();
        }
    };

    // Filter options based on search term
    const filteredOptions = useMemo(() => {
        if (searchable && searchTerm) {
            const query = searchTerm.toLowerCase();
            return options.filter(option =>
                option.text?.toLowerCase().includes(query) ||
                option.value?.toString().toLowerCase().includes(query)
            );
        }
        return options;
    }, [options, searchable, searchTerm]);

    // Get selected option(s) text
    const getSelectedText = () => {
        if (multiple) {
            if (!value || (Array.isArray(value) && value.length === 0)) return placeholder;
            const selected = options.filter(opt => value.includes(opt.value));
            if (selected.length === 0) return placeholder;
            if (selected.length === 1) return selected[0].text;
            return `${selected.length} selected`;
        } else {
            if (!value || value === "") return placeholder;
            const selected = options.find(opt => opt.value === value);
            return selected ? selected.text : placeholder;
        }
    };

    const hasValue = multiple
        ? (Array.isArray(value) && value.length > 0)
        : (value !== null && value !== undefined && value !== "");

    const handleSelect = (optionValue) => {
        if (multiple) {
            const currentValue = Array.isArray(value) ? value : [];
            const newValue = currentValue.includes(optionValue)
                ? currentValue.filter(v => v !== optionValue)
                : [...currentValue, optionValue];
            if (onChange) {
                onChange({ target: { value: newValue } });
            }
        } else {
            if (onChange) {
                onChange({ target: { value: optionValue } });
            }
            setIsOpen(false);
            setSearchTerm("");
            setHighlightedIndex(-1);
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        if (onChange) {
            if (multiple) {
                onChange({ target: { value: [] } });
            } else {
                onChange({ target: { value: "" } });
            }
        }
    };

    const isSelected = (optionValue) => {
        if (multiple) {
            return Array.isArray(value) && value.includes(optionValue);
        }
        return value === optionValue;
    };

    const controlClasses = [
        "ui-select__control",
        isOpen && "ui-select__control--open",
        disabled && "ui-select__control--disabled",
        error && "ui-select__control--error",
        className
    ].filter(Boolean).join(" ");

    useEffect(() => {
        if (isOpen) {
            if (filteredOptions.length === 0) {
                setHighlightedIndex(-1);
                return;
            }

            if (!multiple && hasValue) {
                const selectedIndex = filteredOptions.findIndex(opt => opt.value === value);
                setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
            } else {
                setHighlightedIndex(0);
            }
        } else {
            setHighlightedIndex(-1);
        }
    }, [isOpen, filteredOptions, multiple, hasValue, value]);

    const handleKeyDown = (e) => {
        if (disabled) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!isOpen) {
                handleFocus();
            } else if (filteredOptions.length > 0) {
                setHighlightedIndex(prev => {
                    const next = prev < filteredOptions.length - 1 ? prev + 1 : 0;
                    return next;
                });
            }
            return;
        }

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (!isOpen) {
                handleFocus();
            } else if (filteredOptions.length > 0) {
                setHighlightedIndex(prev => {
                    if (prev === -1 || prev === 0) {
                        return filteredOptions.length - 1;
                    }
                    return prev - 1;
                });
            }
            return;
        }

        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (isOpen) {
                if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                    handleSelect(filteredOptions[highlightedIndex].value);
                }
            } else {
                handleFocus();
            }
            return;
        }

        if (e.key === 'Escape' && isOpen) {
            setIsOpen(false);
            setSearchTerm("");
            setHighlightedIndex(-1);
        }
    };

    return (
        <div className="ui-select" ref={selectRef}>
            {label && (
                <label className="ui-select__label" htmlFor={id}>
                    {label}
                </label>
            )}
            <div
                ref={controlRef}
                className={controlClasses}
                onClick={handleToggle}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                tabIndex={disabled ? -1 : 0}
            >
                <span className={`ui-select__value ${!hasValue ? 'ui-select__value--placeholder' : ''}`}>
                    {getSelectedText()}
                </span>
                <div className="ui-select__actions">
                    {clearable && hasValue && (
                        <button
                            type="button"
                            className="ui-select__clear"
                            onClick={handleClear}
                            aria-label="Clear selection"
                        >
                            <CloseIcon style={{ fontSize: 18 }} />
                        </button>
                    )}
                    <ArrowDropDownIcon
                        className={`ui-select__arrow ${isOpen ? "ui-select__arrow--open" : ""}`}
                    />
                </div>
            </div>

            {isOpen && createPortal(
                <div
                    className="ui-select__dropdown"
                    ref={dropdownRef}
                    style={{
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`,
                        width: `${dropdownPosition.width}px`
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    {searchable && (
                        <div className="ui-select__search">
                            <SearchIcon className="ui-select__search-icon" />
                            <input
                                type="text"
                                className="ui-select__search-input"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setHighlightedIndex(-1);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                autoFocus
                            />
                        </div>
                    )}
                    <div className="ui-select__options">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={option.value}
                                    className={`ui-select__option ${isSelected(option.value) ? "ui-select__option--selected" : ""} ${highlightedIndex === index ? "ui-select__option--highlighted" : ""}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelect(option.value);
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onMouseEnter={() => setHighlightedIndex(index)}
                                >
                                    {multiple && (
                                        <span className="ui-select__checkbox">
                                            {isSelected(option.value) ? "✓" : ""}
                                        </span>
                                    )}
                                    <span className="ui-select__option-text">{option.text}</span>
                                </div>
                            ))
                        ) : (
                            <div className="ui-select__no-results">
                                No options found
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}

            {description && !error && (
                <span className="ui-select__description">{description}</span>
            )}
            {error && <span className="ui-select__error">{error}</span>}
        </div>
    );
};

export default Select;

